/**
 * Who is signed in.
 *
 * The person, the staff role their session acts with, the service areas an
 * admin or a dispatcher is limited to and what the role may do all come
 * from hg-auth (`GET /v2/me`, or the sign-in answer, which carries the same
 * object). Nothing is read out of the access token.
 */
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

import type { components } from "@/shared/api/auth.schema";
import { authApi, unwrap } from "@/shared/api/clients";

import { staffRoleOf, type StaffRole } from "./roles";
import { hasSession, subscribeSession } from "./session";

export type Me = components["schemas"]["MeResponse"];

export const meQueryOptions = queryOptions({
  queryKey: ["auth", "me"] as const,
  queryFn: async ({ signal }): Promise<Me> => {
    const { data } = await unwrap(authApi.GET("/v2/me", { signal }));
    return data;
  },
  staleTime: 5 * 60_000,
});

export interface CurrentStaff {
  /** Null until hg-auth has answered, and when nobody is signed in. */
  me: Me | null;
  /** The staff role. Null means no access: not staff, or a role this build does not know. */
  role: StaffRole | null;
  /** Ids of the service areas the person is limited to. Empty when there is no limit, or no area yet. */
  areas: readonly string[];
  /** hg-auth names no limit (`area_ids: null`): a superadmin. An empty `areas` then means "all", not "none". */
  allAreas: boolean;
  isPending: boolean;
  /** A `ProblemError` unless something broke in the app itself. */
  error: Error | null;
}

export function useSignedIn(): boolean {
  return useSyncExternalStore(subscribeSession, hasSession);
}

const NO_AREAS: readonly string[] = Object.freeze([]);

export function useMe(): CurrentStaff {
  const signedIn = useSignedIn();
  const query = useQuery({ ...meQueryOptions, enabled: signedIn });

  const me = signedIn ? (query.data ?? null) : null;
  const role = me ? staffRoleOf(me) : null;
  return {
    me,
    role,
    areas: me?.area_ids ?? NO_AREAS,
    allAreas: role !== null && me?.area_ids == null,
    isPending: signedIn && query.isPending,
    error: query.error,
  };
}
